import { useCurrency } from '@/utils/hooks/useCurrency';
import { useCurrencySymbol } from '@/utils/hooks/useCurrencySymbol';
import useLanguage from '@/utils/hooks/useLanguage';
import { useNumberFormatter } from 'react-aria';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

interface ChartData {
    name: string;
    value: number;
    color: string;
    percentage: number;
}

interface PortfolioChartProps {
    title: string;
    data: ChartData[];
    totalValue: number;
    baseCurrency: string;
}

// TooltipProps<number, string>
const CustomTooltip = ({ active, payload, symbol, formatCurrency }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload as ChartData;
        return (
            <div className="bg-brand-background p-3 border border-brand-neutral-700 shadow-lg rounded-md text-sm">
                <p className="font-semibold text-brand-text">{data.name}</p>
                <div className="flex items-center gap-4 mt-1">
                    <span className="text-brand-text">Value:</span>
                    <span className="font-medium text-brand-text">
                        {symbol}
                        {formatCurrency.format(data.value)}
                    </span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-brand-text">Share:</span>
                    <span className="font-medium text-brand-text">{data.percentage.toFixed(1)}%</span>
                </div>
            </div>
        );
    }
    return null;
};

export function PortfolioChart({ title, data, totalValue, baseCurrency }: PortfolioChartProps) {
    const lang = useLanguage();

    const symbol = useCurrencySymbol(baseCurrency);
    const numberFormat = useNumberFormatter({
        notation: 'compact',
        compactDisplay: 'short'
    });
    const currency = useCurrency(baseCurrency);
    const formatCurrency = useNumberFormatter({
        minimumFractionDigits: currency.order_decimals,
        maximumFractionDigits: currency.order_decimals
    });

    return (
        <div className="bg-brand-background-dark rounded-xl border border-brand-neutral-700 flex flex-col h-full overflow-hidden">
            <div className="px-6 py-4 border-b border-brand-neutral-700">
                <h3 className="text-lg font-semibold text-brand-text">{title}</h3>
            </div>

            <div className="p-6 flex-1 flex flex-col items-center">
                <div className="w-full h-64 relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data as any}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={2}
                                dataKey="absValue">
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                                ))}
                            </Pie>
                            <Tooltip
                                content={<CustomTooltip symbol={symbol} formatCurrency={formatCurrency} />}
                                wrapperStyle={{
                                    zIndex: 1000
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    {/* Center Text */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-xs text-brand-text font-medium uppercase tracking-wider">
                            {lang.orderForm.total}
                        </span>
                        <span className="text-lg font-bold text-brand-text">
                            {symbol}
                            {numberFormat.format(totalValue)}
                        </span>
                    </div>
                </div>

                {/* Custom Legend */}
                <div className="w-full mt-6 space-y-3">
                    {data.map((item) => {
                        return (
                            <div key={item.name} className="flex items-center justify-between text-sm group">
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <div
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{
                                            backgroundColor: item.color
                                        }}
                                    />
                                    <span className="text-brand-text truncate group-hover:text-brand-text transition-colors">
                                        {item.name}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-brand-text font-medium tabular-nums">
                                        {symbol}
                                        {formatCurrency.format(item.value)}
                                    </span>
                                    <span className="text-brand-neutral-400 w-12 text-right tabular-nums text-xs">
                                        {(item.percentage || 0).toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
